/**
 * 服务授权Tab字段描述配置
 * 定义服务授权tab中token字段的显示配置
 */

import React from "react";
import { EditOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import type { DescriptionItem } from "@ms/shared-components/comps/CompsDescription";
import type { ButtonConfig } from "@ms/shared-components/containers/ContainerPanel";

// Token字段配置接口
export interface TokenFieldConfig {
  // 字段名（支持驼峰和下划线两种命名）
  fieldName: string | string[];
  // 显示标签
  label: string;
  // 是否必显示（false表示仅在值存在时显示）
  required?: boolean;
  // 值转换函数
  valueFormatter?: (value: any, token: any) => string;
  // 跨列数
  span?: number;
}

// Token字段配置列表（按显示顺序）
export const TOKEN_FIELD_CONFIGS: TokenFieldConfig[] = [
  {
    fieldName: ["serviceName", "service_name"],
    label: "服务名称",
  },
  {
    fieldName: ["targetService", "target_service"],
    label: "目标服务",
  },
  {
    fieldName: ["ipWhitelist", "ip_whitelist"],
    label: "IP白名单",
    required: true,
    span: 2, // IP白名单可能较长，占满整行
    valueFormatter: (value) => {
      if (!value || value === null || value === "") {
        return "无限制";
      }
      return String(value);
    },
  },

  {
    fieldName: ["expiresAt", "expires_at"],
    label: "有效期",
    required: true,
    valueFormatter: (value) => {
      if (!value || value === null || value === "") {
        return "永不过期";
      }
      try {
        return new Date(value).toLocaleString("zh-CN");
      } catch (e) {
        return value || "永不过期";
      }
    },
  },
  {
    fieldName: ["isActive", "is_active"],
    label: "状态",
    required: true,
    valueFormatter: (value) => (value ? "启用" : "禁用"),
  },
  {
    fieldName: ["maxUsage", "max_usage"],
    label: "最多使用次数",
    required: true,
    valueFormatter: (value) => {
      if (value === null || value === undefined || value === "") {
        return "不限制";
      }
      return String(value);
    },
  },

  {
    fieldName: ["usageCount", "usage_count"],
    label: "已使用次数",
    required: true,
    valueFormatter: (value) => String(value || 0),
  },
  {
    fieldName: ["lastUsedAt", "last_used_at"],
    label: "最后使用时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },

  {
    fieldName: ["createdAt", "created_at"],
    label: "创建时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },
  {
    fieldName: ["updatedAt", "updated_at"],
    label: "更新时间",
    valueFormatter: (value) => (value ? new Date(value).toLocaleString("zh-CN") : ""),
  },
  {
    fieldName: ["tokenValue", "token_value"],
    label: "Token值",
    span: 2, // Token值较长，占满整行
    valueFormatter: (value) => (value && value.length > 50 ? `${value.substring(0, 50)}...` : value || ""),
  },
];

// 空状态配置
export const EMPTY_TOKEN_CONFIG = {
  key: "noTokens",
  label: "授权信息",
  value: "暂无授权服务",
} as const;

// Token描述组件边框配置
export const TOKEN_DESCRIPTION_BORDERED = false;

/**
 * 从token对象中获取字段值（支持驼峰和下划线两种命名）
 */
export const getTokenFieldValue = (token: any, fieldName: string | string[]): any => {
  if (Array.isArray(fieldName)) {
    // 尝试多个字段名
    for (const name of fieldName) {
      if (token[name] !== undefined && token[name] !== null) {
        return token[name];
      }
    }
    return undefined;
  } else {
    return token[fieldName];
  }
};

/**
 * 构建token的标题
 */
export const buildTokenTitle = (token: any, index: number): string => {
  const targetService = getTokenFieldValue(token, ["targetService", "target_service"]);
  const serviceName = getTokenFieldValue(token, ["serviceName", "service_name"]);
  return targetService || serviceName || `Token ${index + 1}`;
};

/**
 * 根据配置构建token字段的描述项
 */
export const buildTokenDescriptionItems = (token: any, index: number): DescriptionItem[] => {
  const items: DescriptionItem[] = [];

  // 添加标题
  const tokenTitle = buildTokenTitle(token, index);
  items.push({
    key: `token-${index}-title`,
    label: tokenTitle,
    value: "",
    span: 2, // 标题占满整行
  });

  // 遍历字段配置
  TOKEN_FIELD_CONFIGS.forEach((config) => {
    let value = getTokenFieldValue(token, config.fieldName);

    // 处理默认值：isActive 和 usageCount 如果没有值，使用默认值
    if (config.fieldName === "isActive" || (Array.isArray(config.fieldName) && config.fieldName.includes("isActive"))) {
      if (value === undefined || value === null) {
        // 尝试下划线命名
        value = token.is_active !== undefined ? token.is_active : true;
      }
    } else if (
      config.fieldName === "usageCount" ||
      (Array.isArray(config.fieldName) && config.fieldName.includes("usageCount"))
    ) {
      if (value === undefined || value === null) {
        // 尝试下划线命名
        value = token.usage_count !== undefined ? token.usage_count : 0;
      }
    }

    // 判断是否显示该字段
    const shouldShow = config.required || (value !== undefined && value !== null && value !== "");

    if (shouldShow) {
      // 格式化值
      let formattedValue = value;
      if (config.valueFormatter) {
        formattedValue = config.valueFormatter(value, token);
      } else if (value !== undefined && value !== null) {
        formattedValue = String(value);
      }

      // 添加描述项
      items.push({
        key: `token-${index}-${Array.isArray(config.fieldName) ? config.fieldName[0] : config.fieldName}`,
        label: config.label,
        value: formattedValue,
        span: config.span,
      });
    }
  });

  return items;
};

/**
 * 构建分隔线描述项
 */
export const buildSeparatorItem = (index: number): DescriptionItem => ({
  key: `token-${index}-separator`,
  label: "",
  value: "",
});

// Token卡片按钮配置接口
export interface TokenCardButtonHandlers {
  onEdit?: (token: any) => void;
  onCopy?: (token: any) => Promise<void>;
  onDelete?: (tokenId: string) => void;
}

/**
 * 构建Token卡片的按钮配置
 * @param token Token对象
 * @param handlers 按钮操作处理器
 * @returns 按钮配置数组
 */
export const buildTokenCardButtons = (
  token: any,
  handlers: TokenCardButtonHandlers
): ButtonConfig[] => {
  return [
    {
      key: "edit",
      text: "编辑",
      type: "text",
      icon: React.createElement(EditOutlined),
      onClick: () => {
        handlers.onEdit?.(token);
      },
      group: "primary",
    },
    {
      key: "copy",
      text: "复制",
      type: "text",
      icon: React.createElement(CopyOutlined),
      onClick: async () => {
        await handlers.onCopy?.(token);
      },
      group: "primary",
    },
    {
      key: "delete",
      text: "删除",
      type: "text",
      danger: true,
      icon: React.createElement(DeleteOutlined),
      onClick: () => {
        if (token.id) {
          handlers.onDelete?.(token.id);
        }
      },
      group: "primary",
    },
  ];
};

